import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const improvado: Competitor = {
  "id": "improvado",
  "name": "Improvado",
  "group": "ecomm",
  "website": "https://improvado.io",
  "owner": "Independent, venture-backed (founded 2015, San Diego; $22M Series A led by Updata Partners in 2022). No acquisition found.",
  "summary": "An enterprise marketing data platform that extracts data from 500+ marketing, sales and commerce sources, transforms it, and loads it into the customer's warehouse or BI tool, with an AI agent and optional attribution, MMM and incrementality add-ons. It is built for larger brands and agencies.",
  "bestFor": [
    "Mid-market and enterprise brands or agencies with many ad accounts and sources, and budget for a $20k+/year platform",
    "Teams that want their marketing and commerce data in their own Snowflake, BigQuery, Databricks or Redshift without building pipelines",
    "Brands that want vendor-run MMM, incrementality and multi-touch attribution as an add-on rather than an in-house data science project"
  ],
  "notFor": [
    "Small DTC brands: managed plans start at $20,000/year",
    "Teams that mainly want an out-of-the-box Shopify profit or contribution-margin dashboard",
    "Brands needing TikTok Shop order data (no TikTok Shop connector was found)"
  ],
  "strengths": [
    "A large maintained connector library (500+ sources), including Shopify (GraphQL), Amazon Seller Central (SP-API), Oracle NetSuite and Recharge, with no per-connector fees",
    "Delivers into a warehouse you control (Snowflake, BigQuery, Databricks, Redshift, ClickHouse and others), so it can act as the ingestion and normalization layer instead of hand-built ELT",
    "Advanced Analytics add-ons cover marketing mix modeling, incrementality testing and multi-touch attribution, which are costly to build and staff in-house",
    "Professional Services credits are included on Growth and Enterprise, so some modeling work is done by the vendor",
    "An AI Agent answers natural-language questions, builds dashboards and, since January 2026, can query data directly in your BigQuery, Snowflake or MySQL destination",
    "Governance features (data governance rules), SOC 2 Type II on all plans, and HIPAA on Growth and above"
  ],
  "limitations": [
    "High entry price for managed plans: Starter from $20,000/year for 20M rows/year; Growth from $41,700/year",
    "Amazon Seller Central refunds are available per day but not per ASIN per day, and the latest day's refunds are understated until it settles",
    "The Shopify 'Sales Agreement by Order' report has no gross, net or returns fields, so summing it will not match Shopify's Sales report; proper sales and returns modeling is still needed",
    "Full Shopify order history requires the read_all_orders scope, which needs separate Shopify approval; otherwise only the last 60 days are available",
    "No TikTok Shop connector was found in the documentation",
    "Cost is tied to annual row volume and sync frequency, so high-volume order and event data can push a brand into higher tiers",
    "No prebuilt COGS or contribution-margin model is documented for ecommerce; Improvado's Shopify dashboard example lists revenue, AOV, CLV and ROAS but not profit"
  ],
  "pricing": {
    "model": "Tiered annual subscription priced by rows per year and sync frequency; add-ons priced separately",
    "publicStartingPrice": "$100/month (MCP Only, 2M rows/year); managed platform from $20,000/year (Starter)",
    "freeTier": "no - the earlier Free Limited tier was removed in October 2026 (per a third-party pricing tracker)",
    "notes": "MCP Only: $100/mo, 2M rows/yr, daily sync, 300 MCP actions/week, connects your own AI assistant. Starter: from $20,000/yr, 20M rows/yr, daily sync, all connectors and destinations, 3 governance rules. Growth: from $41,700/yr, 60M-200M rows/yr, daily to twice-daily sync, up to 10 workspaces, Professional Services credits, HIPAA. Enterprise: custom quote, 1B rows/yr, custom sync, unlimited workspaces, SSO. Add-ons: Advanced Analytics (MMM, incrementality, MTA) from $20k/yr; Creative Analytics from $4k/yr. The main cost drivers are rows per year and sync frequency. Connectors and destinations are unlimited and not charged separately, and price does not depend on ad spend. A third-party tracker (PricingSaaS) recorded Growth at $50k/yr during a late-October 2026 repackaging; the vendor page showed 'from $41,700/yr' on 2026-11-03. The vendor says its Amazon connector setup takes under five minutes; no overall onboarding time is published.",
    "sourceUrl": "https://improvado.io/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "shopify": {
      "value": "yes",
      "note": "Shopify GraphQL connector covering orders, refunds, transactions, payouts, products and customers.",
      "sourceUrl": "https://improvado.io/docs/shopify-graphql"
    },
    "amazon": {
      "value": "yes",
      "note": "SP-API connector covering orders, inventory, financial events and FBA shipments. Refunds are not available per ASIN.",
      "sourceUrl": "https://improvado.io/docs/amazon-seller-central"
    },
    "tiktok_shop": {
      "value": "unknown",
      "note": "No TikTok Shop connector was found; TikTok Ads is supported.",
      "sourceUrl": "https://improvado.io/docs/tiktok-ads"
    },
    "wholesale_retail_or_erp": {
      "value": "yes",
      "note": "Oracle NetSuite connector covering transactions, invoices, journal entries and inventory.",
      "sourceUrl": "https://improvado.io/integrations/oracle-netsuite"
    },
    "ad_platforms": {
      "value": "yes",
      "note": "Meta, Google and TikTok Ads, among 500+ sources.",
      "sourceUrl": "https://improvado.io/docs/tiktok-ads"
    },
    "first_party_pixel_attribution": {
      "value": "partial",
      "note": "Multi-touch attribution is offered in the paid Advanced Analytics add-on. A proprietary tracking pixel was not confirmed.",
      "sourceUrl": "https://improvado.io/pricing"
    },
    "mmm_incrementality": {
      "value": "yes",
      "note": "MMM and incrementality testing in the Advanced Analytics add-on (from $20k/yr; Growth and Enterprise).",
      "sourceUrl": "https://improvado.io/pricing"
    },
    "profit_cogs": {
      "value": "unknown",
      "note": "No prebuilt COGS or contribution-margin model is documented. Data could be modeled with transformations.",
      "sourceUrl": "https://improvado.io/dashboards/build-shopify-dashboard"
    },
    "ltv_cohorts": {
      "value": "partial",
      "note": "Customer lifetime value is listed in the Shopify dashboard example. Cohort or retention analysis is not mentioned.",
      "sourceUrl": "https://improvado.io/dashboards/build-shopify-dashboard"
    },
    "subscription_analytics": {
      "value": "partial",
      "note": "A Recharge Payments connector (subscriptions, charges, orders) delivers raw data. Prebuilt subscription analytics were not found.",
      "sourceUrl": "https://improvado.io/docs/recharge-payments"
    },
    "warehouse_access": {
      "value": "yes",
      "note": "Loads into Snowflake, BigQuery, Databricks, Redshift, ClickHouse and other warehouses you control.",
      "sourceUrl": "https://improvado.io/pricing"
    },
    "custom_metrics": {
      "value": "yes",
      "note": "Transformation recipes with filters, joins, sources and mappings model data into analysis-ready tables; governance rules are also available.",
      "sourceUrl": "https://improvado.io/product-updates/recipe-logic-flexibility"
    },
    "custom_sql": {
      "value": "unknown",
      "note": "Vendor materials stress no-SQL use, with the agent writing queries. A user-facing SQL editor was not confirmed.",
      "sourceUrl": "https://improvado.io/dashboards/build-shopify-dashboard"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "The AI Agent answers natural-language questions and builds dashboards. MCP works with Claude, ChatGPT and Cursor.",
      "sourceUrl": "https://improvado.io/pricing"
    },
    "mobile_app": {
      "value": "unknown",
      "note": "No mobile app was found.",
      "sourceUrl": "https://improvado.io/pricing"
    }
  },
  "recentChanges": [
    {
      "date": "2025-09",
      "change": "A major AI Agent update added business-aware insights, instant visualizations, scheduled workflows and MCP support.",
      "sourceUrl": "https://www.newswire.com/news/improvado-unveils-next-generation-ai-agent-for-marketing-analytics-22645675"
    },
    {
      "date": "2025-10",
      "change": "Launched native AI-built dashboards and automated scheduled reporting, positioning Improvado as an end-to-end marketing intelligence platform.",
      "sourceUrl": "https://business.ricentral.com/ricentral/article/accwirecq-2025-10-15-improvado-becomes-an-end-to-end-platform-for-enterprise-and-agency-marketing-intelligence"
    },
    {
      "date": "2026-01",
      "change": "The AI Agent can now query data directly in connected BigQuery, Snowflake and MySQL destinations.",
      "sourceUrl": "https://improvado.io/product-updates/query-destination-tool"
    },
    {
      "date": "2026-10",
      "change": "Pricing was repackaged: a published Starter ($20k/yr) and Growth tier replaced Advanced (custom), the Free Limited tier was removed, and an Advanced Analytics add-on was introduced (third-party pricing tracker).",
      "sourceUrl": "https://pricingsaas.com/companies/improvado/diffs/2026W44"
    }
  ],
  "sources": [
    {
      "title": "Improvado pricing",
      "url": "https://improvado.io/pricing"
    },
    {
      "title": "Improvado docs: Shopify GraphQL",
      "url": "https://improvado.io/docs/shopify-graphql"
    },
    {
      "title": "Improvado docs: Amazon Seller Central",
      "url": "https://improvado.io/docs/amazon-seller-central"
    },
    {
      "title": "Improvado Amazon Seller Central integration",
      "url": "https://improvado.io/integrations/amazon-seller-central"
    },
    {
      "title": "Improvado Oracle NetSuite integration",
      "url": "https://improvado.io/integrations/oracle-netsuite"
    },
    {
      "title": "Improvado docs: Recharge Payments",
      "url": "https://improvado.io/docs/recharge-payments"
    },
    {
      "title": "Improvado docs: TikTok Ads",
      "url": "https://improvado.io/docs/tiktok-ads"
    },
    {
      "title": "Improvado docs: ClickHouse destination",
      "url": "https://improvado.io/docs/clickhouse"
    },
    {
      "title": "Improvado Shopify dashboard page",
      "url": "https://improvado.io/dashboards/build-shopify-dashboard"
    },
    {
      "title": "Improvado product update: Query destination tool",
      "url": "https://improvado.io/product-updates/query-destination-tool"
    },
    {
      "title": "Improvado product update: Recipe logic flexibility",
      "url": "https://improvado.io/product-updates/recipe-logic-flexibility"
    },
    {
      "title": "Newswire: Improvado next-generation AI Agent (Sept 2025)",
      "url": "https://www.newswire.com/news/improvado-unveils-next-generation-ai-agent-for-marketing-analytics-22645675"
    },
    {
      "title": "Press release: Improvado end-to-end platform (Oct 2025)",
      "url": "https://business.ricentral.com/ricentral/article/accwirecq-2025-10-15-improvado-becomes-an-end-to-end-platform-for-enterprise-and-agency-marketing-intelligence"
    },
    {
      "title": "Dealroom: Improvado (third-party)",
      "url": "https://dealroom.co/companies/improvado/"
    },
    {
      "title": "PricingSaaS: Improvado pricing diff 2026 W44 (third-party)",
      "url": "https://pricingsaas.com/companies/improvado/diffs/2026W44"
    }
  ],
  "lastReviewed": "2026-11-03"
};
